import classNames from 'classnames';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { BotIcon } from '@/components/ui/bot-icon';
import { Segmented, StateMessage } from '@/components/ui/controls';
import { NotYet, NotYetInfo, notYet } from '@/components/ui/not-yet';
import { Badge, PageHeader, PreviewBanner } from '@/components/ui/page';
import { btn, field } from '@/components/ui/styles';
import { leadSearchLink } from '@/mocks/shared';
import type { InboxChannel, InboxThread } from '@/mocks/inbox';
import { formatDateTime, formatRelative, initials } from '@/utils/format';

import ChannelNav from './channel-nav';
import { useClosing } from '@/utils/use-closing';

type Filter = 'needs_reply' | 'new_lead' | 'all';

const CHANNEL_LABEL: Record<InboxChannel, string> = { email: 'Email', instagram: 'Instagram direct message' };

const hasDraft = (t: InboxThread) => t.messages.some((m) => m.bot === 'draft');

/** Where a thread stands: waiting for us, a sender who is not a lead yet, or answered. */
const ThreadState = ({ thread }: { thread: InboxThread }) => {
  if (thread.status === 'answered') return <span className="text-xs text-fg-muted">Answered</span>;
  if (thread.status === 'new_lead')
    return <span className="text-xs text-fg">{thread.leadEmail ? 'New lead' : 'Not a lead yet'}{hasDraft(thread) ? ' · bot draft to confirm' : ''}</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-fg">
      <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 text-status-warning" fill="currentColor" aria-hidden>
        <path d="M8 1.5 15 14H1z" />
      </svg>
      Needs a reply{hasDraft(thread) ? ' · bot draft to confirm' : ''}
    </span>
  );
};

/** One thread beside the list: who wrote, the conversation, the bot's drafts to confirm (REM-2, REM-3) and a reply box. */
const ThreadPanel = ({ thread, onClose, onChange }: { thread: InboxThread; onClose: () => void; onChange: (t: InboxThread) => void }) => {
  const [closing, close] = useClosing(onClose);
  const [reply, setReply] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const approve = (id: string) =>
    onChange({ ...thread, status: 'answered', messages: thread.messages.map((m) => (m.id === id ? { ...m, bot: 'sent' } : m)) });
  const discard = (id: string) => onChange({ ...thread, messages: thread.messages.filter((m) => m.id !== id) });
  const send = (e: FormEvent) => {
    e.preventDefault();
    if (!reply.trim()) return;
    const message = { id: `${thread.id}-${thread.messages.length + 1}`, from: 'You', fromTeam: true, text: reply.trim(), sentAt: new Date().toISOString() };
    onChange({ ...thread, status: 'answered', messages: [...thread.messages, message] });
    setReply('');
  };

  return (
    <aside aria-label="Conversation" className={`fixed inset-y-0 right-0 z-40 flex w-full max-w-[440px] flex-col bg-surface-raised shadow-e3 ${closing ? 'animate-drawer-out-right' : 'animate-drawer-in-right'}`}>
      <div className="flex items-start gap-3 px-5 py-4">
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-fg-strong">{thread.name}</p>
          <p className="truncate text-xs text-fg-muted">
            {CHANNEL_LABEL[thread.channel]} · {thread.contact}
          </p>
          {thread.subject && <p className="mt-1 truncate text-sm text-fg">{thread.subject}</p>}
        </div>
        <button type="button" onClick={close} aria-label="Close conversation" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-fg-muted hover:bg-surface-hover hover:text-fg md:h-8 md:w-8">
          <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>

      <section className="border-t border-line px-5 py-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-medium uppercase tracking-wide text-fg-muted">Client</h3>
          <ThreadState thread={thread} />
        </div>
        {thread.leadEmail ? (
          <Link to={leadSearchLink(thread.leadEmail)} className="text-sm text-brand hover:underline">
            {thread.name} · {thread.leadEmail}
          </Link>
        ) : thread.channel === 'email' ? (
          <div className="space-y-2">
            <p className="text-sm text-fg-muted">Not a lead yet. A first question by email creates one, even without a registration (LEAD-6).</p>
            <button type="button" className={btn.secondary} onClick={() => onChange({ ...thread, leadEmail: thread.contact })}>
              Create the lead
            </button>
          </div>
        ) : (
          <p className="text-sm text-fg-muted">Not a lead yet. The bot asked for their email; the lead is created once they give it (LEAD-6).</p>
        )}
      </section>

      <section className="flex min-h-0 flex-1 flex-col border-t border-line">
        <h3 className="px-5 pt-4 text-xs font-medium uppercase tracking-wide text-fg-muted">Conversation ({thread.messages.length})</h3>
        <ol className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-3">
          {thread.messages.map((m) => (
            <li key={m.id} className={classNames('flex flex-col', m.fromTeam ? 'items-end' : 'items-start')}>
              <span className="mb-0.5 inline-flex items-center gap-1 text-xs text-fg-faint">
                {m.bot && <BotIcon />}
                {m.from}
                {m.fromTeam && !m.bot ? ' (team)' : ''} · {formatDateTime(m.sentAt)}
              </span>
              {m.bot === 'draft' && (
                <span className="mb-1">
                  <Badge tone="info">Draft · a human will confirm</Badge>
                </span>
              )}
              <p
                className={classNames(
                  'max-w-[85%] whitespace-pre-wrap break-words rounded-xl px-3 py-2 text-sm text-fg',
                  m.bot ? 'bg-info/15' : m.fromTeam ? 'bg-brand/10' : 'bg-surface-inset',
                )}
              >
                {m.text}
              </p>
              {m.bot === 'draft' && (
                <span className="mt-1.5 flex gap-1">
                  <button type="button" className={btn.quiet} onClick={() => discard(m.id)}>
                    Discard
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-11 items-center rounded-lg bg-brand px-3 text-sm text-black shadow-ctl transition hover:shadow-ctl-hover active:translate-y-px active:shadow-ctl-press hover:bg-cta-hover-bg hover:text-cta-hover-fg md:h-8"
                    onClick={() => approve(m.id)}
                  >
                    Confirm and send
                  </button>
                </span>
              )}
            </li>
          ))}
        </ol>
        <form onSubmit={send} className="border-t border-line px-5 py-3">
          <label className="sr-only" htmlFor="inbox-reply">
            Reply
          </label>
          <textarea id="inbox-reply" rows={3} className={field.textarea} placeholder={`Reply to ${thread.name}`} value={reply} onChange={(e) => setReply(e.target.value)} />
          <div className="mt-2 flex justify-end">
            <button type="submit" className={btn.primary} disabled={!reply.trim()}>
              Send reply
            </button>
          </div>
        </form>
      </section>
    </aside>
  );
};

/**
 * A preview inbox for one channel, in the Telegram page's layout: the threads,
 * and the selected one beside them (LEAD-6, REM-2, REM-3). Made-up threads; every
 * change stays in this page's state.
 */
const InboxPage = ({
  description,
  initial,
  requirements,
  empty,
}: {
  description: string;
  initial: InboxThread[];
  requirements: string[];
  empty: string;
}) => {
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState(initial);
  const filter = (params.get('status') as Filter) || 'needs_reply';
  const selected = params.get('thread');

  const update = (patch: Record<string, string | null>) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );

  const matches = (t: InboxThread, f: Filter) => f === 'all' || t.status === f;
  const count = (f: Filter) => threads.filter((t) => matches(t, f)).length;
  const shown = threads.filter((t) => matches(t, filter));
  const thread = threads.find((t) => t.id === selected);
  const last = (t: InboxThread) => t.messages[t.messages.length - 1];

  return (
    <div className={classNames('transition-[padding]', thread && 'xl:pr-[452px]')}>
      <PageHeader title="Conversations" description={description}>
        <ChannelNav />
      </PageHeader>
      <div className="flex items-start gap-3">
        <div className={classNames('min-w-0 flex-1', notYet)}>
          <PreviewBanner requirements={requirements} />
        </div>
        <NotYetInfo what="instagram" className="mt-3.5" />
      </div>
      <NotYet what="instagram" info={false}>
        <div className="mb-4">
          <Segmented<Filter>
            label="Show"
            value={filter}
            onChange={(v) => update({ status: v === 'needs_reply' ? null : v })}
            options={[
              { value: 'needs_reply', label: `Needs a reply (${count('needs_reply')})` },
              { value: 'new_lead', label: `New people (${count('new_lead')})` },
              { value: 'all', label: `All (${threads.length})` },
            ]}
          />
        </div>

        {shown.length === 0 ? (
          <StateMessage title={filter === 'needs_reply' ? 'Nothing waits for a reply' : 'No conversations here'}>{empty}</StateMessage>
        ) : (
          <ul className="overflow-hidden rounded-card bg-surface shadow-e1">
            {shown.map((t) => (
              <li key={t.id} className="border-b border-line last:border-0">
                <button
                  type="button"
                  onClick={() => update({ thread: t.id })}
                  aria-current={selected === t.id}
                  className={classNames('flex w-full items-start gap-3 px-4 py-3 text-left transition-colors', selected === t.id ? 'bg-brand/10' : 'hover:bg-surface-hover')}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-inset text-xs text-fg-muted" aria-hidden>
                    {initials(t.name, t.contact.replace(/^@/, ''))}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="truncate text-fg-strong">{t.name}</p>
                      <span className="shrink-0 text-xs text-fg-faint">{formatRelative(last(t).sentAt)}</span>
                    </div>
                    {t.subject && <p className="truncate text-sm text-fg">{t.subject}</p>}
                    <ThreadState thread={t} />
                    <p className="mt-1 truncate text-sm text-fg-muted">
                      <span className="text-fg">{last(t).from}: </span>
                      {last(t).text}
                    </p>
                  </div>
                  <span className="shrink-0 self-center text-xs tabular-nums text-fg-faint">{t.messages.length} msgs</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </NotYet>

      {/* Outside the fade: a drawer inside it would be faded and stacked under the page's bars. */}
      {thread && (
        <ThreadPanel
          thread={thread}
          onClose={() => update({ thread: null })}
          onChange={(next) => setThreads((ts) => ts.map((t) => (t.id === next.id ? next : t)))}
        />
      )}
    </div>
  );
};

export default InboxPage;

// The All channels page opens these threads too.
export { ThreadPanel as InboxThreadPanel };
